import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../hooks/apiHook";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

const KycDetails = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedCategory] = useState("1"); // Fixed as per your upload logic
  const [documentFront, setDocumentFront] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const fileInputRef = useRef(null);

  // ------------------------------------- API -------------------------------------
  const details = ApiHook.CallKycDetails();
  const kycMutation = ApiHook.CallKycUploads();
  const kycFileDeleteMutation = ApiHook.CallDeleteKycFiles();

  // ------------------------------- Functionalities -------------------------------
  const [userKyc, setUserKyc] = useState(details?.data?.userKyc);

  useEffect(() => {
    setUserKyc(details?.data?.userKyc);
  }, [details?.data?.userKyc]);

  const getFileKey = (file) => {
    return `${file.name}-${file.size}-${file.lastModified}`;
  };

  const handleDocumentFrontChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const allFiles = [...documentFront, ...newFiles];
    const validFiles = allFiles.slice(0, 4);

    const filteredFiles = validFiles.filter(file =>
      ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
    );

    if (filteredFiles.length > 4) {
      toast.error('Maximum 4 files allowed');
      return;
    }

    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setDocumentFront(filteredFiles);
    const newPreviewUrls = filteredFiles.map(file => ({
      url: URL.createObjectURL(file),
      key: getFileKey(file)
    }));
    setPreviewUrls(newPreviewUrls);
    event.target.value = null;
  };

  const removePreview = (keyToRemove) => {
    const previewToRemove = previewUrls.find(preview => preview.key === keyToRemove);
    const newPreviews = previewUrls.filter(preview => preview.key !== keyToRemove);
    const newFiles = documentFront.filter(file => getFileKey(file) !== keyToRemove);

    URL.revokeObjectURL(previewToRemove.url);
    setDocumentFront(newFiles);
    setPreviewUrls(newPreviews);
  };

  const resetAll = () => {
    previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
    setDocumentFront([]);
    setPreviewUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleUploadClick = () => {
    const files = {
      files: documentFront,
      category: selectedCategory,
      type: "kyc",
    };
    kycMutation.mutate(files, {
      onSuccess: (res) => {
        if (res?.status) {
          queryClient.invalidateQueries({ queryKey: ["kyc-details"] });
          toast.success(t(res?.data));
          resetAll();
        } else {
          if (res.data.code === 1124) {
            toast.error(t("kycVerified"));
          } else if (res.data.code) {
            toast.error(t(res?.data?.description));
          } else {
            toast.error(res?.message);
          }
        }
      },
      onError: (error) => {
        toast.error(t("upload_failed"));
        console.error('Upload error:', error);
      }
    });
  };

  const deleteRejectedFile = (file) => {
    const files = [file];
    kycFileDeleteMutation.mutate(files, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["kyc-details"] });
        toast.success(t("file_deleted_successfully"));
      }
    });
  };

  const pendingFiles = (files) => {
    return files.filter(image => image.status === 2).map(image => image.id);
  };

  const deletePendingFiles = (files) => {
    const filesId = pendingFiles(files);
    if (filesId.length > 0) {
      kycFileDeleteMutation.mutate(filesId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["kyc-details"] });
          toast.success(t("pending_files_deleted"));
        }
      });
    }
  };

  useEffect(() => {
    return () => {
      previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
    };
  }, [previewUrls]);

  return (
    <>
      <div className="page_head_top">{t("kyc_details")}</div>
      <div className="kyc_bg">
        <div className="row">
          <div>Select Documents ( Pan Card | Aadhaar | Bank Passbook | Signature ) ({documentFront.length}/4)</div>
          <div className="col-md-2 mt-3">
            <input
              ref={fileInputRef}
              className="form-control"
              type="file"
              name="documentFront"
              id="documentFront"
              accept="image/jpeg, image/png, image/jpg"
              multiple
              onChange={handleDocumentFrontChange}
              disabled={documentFront.length >= 4 || kycMutation.isLoading}
            />
            <p className="info_txt">({t('allowedTypes')}) - Max 4 files</p>
          </div>

          {previewUrls.length > 0 && (
            <div className="d-flex flex-wrap">
              {previewUrls.map((preview) => (
                <div 
                  key={preview.key} 
                  className="preview-container position-relative m-3"
                >
                  <img
                    src={preview.url}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      margin: '10px 0',
                      objectFit: 'contain'
                    }}
                  />
                  <button
                    className="btn text-danger btn-sm position-absolute top-0 end-0 pt-2"
                    onClick={() => removePreview(preview.key)}
                    disabled={kycMutation.isLoading}
                  >
                    <i className="fa fa-circle-xmark fa-xl"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="col-md-3 mt-3">
            <button
              className="btn btn-primary"
              onClick={handleUploadClick}
              disabled={kycMutation.isLoading || documentFront.length === 0}
            >
              {kycMutation.isLoading ? t("uploading") : t("upload")}
            </button>
          </div>
        </div>
      </div>
      <div className="kyc_bg mt-2">
        <div className="table-responsive min-hieght-table">
          <table className="striped table_scroll">
            <thead>
              <tr>
                <th>{t("slno")}</th>
                <th>{t("status")}</th>
                <th>{t("document_files")}</th>
                <th>Reason</th>
                <th>{t("action")}</th>
              </tr>
            </thead>
            <tbody>
              {userKyc?.length > 0 ? (
                userKyc.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>
                      {item.status === "approved" && (
                        <span style={{ color: "green" }}>{t(item.status)}</span>
                      )}
                      {item.status === "pending" && (
                        <span className="bg-warning p-1 px-2 rounded-pill">{t(item.status)}</span>
                      )}
                      {item.status === "rejected" && (
                        <span style={{ color: "red" }}>{t(item.status)}</span>
                      )}
                    </td>
                    <td>
                      <div className="image_view_sec">
                        {item?.files?.map((image, i) => (
                          <div className="image_view" key={image.id || i}>
                            <img 
                              src={image?.file} 
                              alt="kyc"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.classList.add('broken-image');
                              }}
                            />
                          </div>
                        ))}
                        {item?.rejectedFiles?.map((image, i) => (
                          <div className="image_view" key={image.id || i}>
                            <a data-bs-toggle="modal" href="#kycimageview">
                              <img 
                                src={image?.file} 
                                alt="kyc"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.classList.add('broken-image');
                                }} 
                              />
                            </a>
                            <div
                              className="upload_error"
                              onClick={() => deleteRejectedFile(image?.id)}
                            >
                              <i className="fa fa-close"></i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>{item.reason}</td>
                    <td>
                      {item.status === "pending" && (
                        <a
                          href="#"
                          className="action_btn"
                          onClick={(e) => {
                            e.preventDefault();
                            deletePendingFiles(item?.files);
                          }}
                        >
                          <i className="fa fa-trash"></i>
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <div className="nodata-table-view">
                      <div className="nodata-table-view-box">
                        <div className="nodata-table-view-box-img">
                          <img src="/images/no-data-image1.jpg" alt="" />
                        </div>
                        <div className="nodata-table-view-box-txt">
                          {t("sorry_no_data_found")}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default KycDetails;